import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody } from '@img/ui';
import { api, type ApiSuccess } from '../lib/api';
import { SEO } from '../components/SEO';

interface Credential {
  id: string;
  title: string;
  description: string | null;
  imageUrl: string;
  category: string;
  issuedBy: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  referenceNo: string | null;
}

const CATEGORY_LABEL: Record<string, string> = {
  LICENSE: '🪪 رخصة',
  COMMERCIAL_REGISTER: '📋 سجل تجاري',
  CERTIFICATE: '🏅 شهادة',
  OTHER: '📄 وثيقة',
};

/**
 * 📜 وثائقنا وشهاداتنا
 * تعرض تراخيص شركة صورة العقارية + سجلاتها التجارية + شهاداتها بشبكة صور موحّدة الأبعاد.
 */
export function CredentialsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['credentials', 'public'],
    queryFn: () => api.get<ApiSuccess<Credential[]>>('/api/credentials'),
  });
  const items = data?.data ?? [];
  const [openId, setOpenId] = useState<string | null>(null);
  const opened = openId ? items.find((i) => i.id === openId) ?? null : null;

  return (
    <div className="bg-gradient-to-b from-navy-soft/30 to-white">
      <SEO
        title="الاعتمادات والتراخيص الرسمية — شركة صورة العقارية 🏛️"
        description="جميع التراخيص والسجلات الرسمية لشركة صورة العقارية: ترخيص الهيئة العامة للعقار (REGA)، السجل التجاري، شهادات الجودة، اعتماد البورصة العقارية، شهادة التجارة الإلكترونية. شفافية كاملة لكل عملائنا."
        keywords="تراخيص شركة صورة العقارية, اعتمادات شركة عقارية, ترخيص REGA, الهيئة العامة للعقار, السجل التجاري, شهادة التجارة الإلكترونية, البورصة العقارية, شركة عقارية مرخصة, شفافية, موثوقية, ضمان جودة, img.sa"
        canonical="https://img.sa/credentials"
        jsonLd={{
          '@type': 'CollectionPage',
          name: 'الاعتمادات والتراخيص الرسمية',
          url: 'https://img.sa/credentials',
          about: {
            '@type': 'Organization',
            name: 'شركة صورة العقارية',
            url: 'https://img.sa',
            hasCredential: items.slice(0, 10).map((c) => ({
              '@type': 'EducationalOccupationalCredential',
              name: c.title,
              description: c.description ?? undefined,
              credentialCategory: c.category,
              recognizedBy: c.issuedBy ? { '@type': 'Organization', name: c.issuedBy } : undefined,
              dateCreated: c.issuedAt ?? undefined,
              expires: c.expiresAt ?? undefined,
              identifier: c.referenceNo ?? undefined,
            })),
          },
        }}
      />
      {/* Hero */}
      <section className="bg-gradient-to-l from-navy-darker via-navy-dark to-navy text-white py-14 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-6xl mb-3">🏛️</div>
          <h1 className="text-3xl md:text-4xl font-black mb-3">وثائقنا وشهاداتنا</h1>
          <p className="text-gold-light text-base leading-relaxed max-w-2xl mx-auto">
            نفتخر بأن نعمل وفق أعلى معايير الشفافية. كل التراخيص والسجلات الرسمية لـ
            <b> شركة صورة العقارية</b> منشورة هنا للاطّلاع.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {isLoading ? (
          <div className="text-center text-gray-500 py-12">⟳ جاري التحميل…</div>
        ) : items.length === 0 ? (
          <Card><CardBody>
            <div className="text-center py-16 text-gray-500">
              <div className="text-5xl mb-3">📜</div>
              <p>لم تُنشَر وثائق بعد. يتم إضافتها قريباً من قِبل الإدارة.</p>
            </div>
          </CardBody></Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {items.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setOpenId(c.id)}
                className="group bg-white rounded-2xl border border-gray-200 hover:border-navy hover:shadow-xl transition overflow-hidden text-right"
              >
                <div className="px-3 pt-3 pb-2">
                  <h3 className="font-black text-navy-dark text-sm leading-tight line-clamp-2 min-h-[2.5rem]">
                    {c.title}
                  </h3>
                  <div className="text-[11px] text-gray-500 mt-1">{CATEGORY_LABEL[c.category] ?? c.category}</div>
                </div>
                <div className="aspect-square w-full bg-gradient-to-br from-navy-soft/30 to-gray-50 overflow-hidden border-t border-gray-100 relative">
                  <img
                    src={c.imageUrl}
                    alt={c.title}
                    className="w-full h-full object-contain group-hover:scale-105 transition duration-300"
                    loading="lazy"
                    onError={(e) => {
                      // 🖼️ بديل أنيق لو الصورة ما تحمّلت
                      const target = e.currentTarget;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent && !parent.querySelector('.img-fallback')) {
                        const fb = document.createElement('div');
                        fb.className = 'img-fallback absolute inset-0 flex flex-col items-center justify-center text-navy-dark/60 p-4';
                        fb.innerHTML = `
                          <div class="text-5xl mb-2">📜</div>
                          <div class="text-xs font-bold text-center">${CATEGORY_LABEL[c.category] ?? c.category}</div>
                          <div class="text-[10px] text-gray-400 mt-1 text-center">معتمد ومسجّل لدى الجهات الرسمية</div>
                        `;
                        parent.appendChild(fb);
                      }
                    }}
                  />
                </div>
                <div className="px-3 py-2 text-[11px] text-navy bg-navy/5 group-hover:bg-navy group-hover:text-white transition text-center">
                  اضغط لعرض الوثيقة كاملة ←
                </div>
              </button>
            ))}
          </div>
        )}

        {/* بانر شفافية */}
        <div className="mt-10">
          <Card>
            <CardBody className="text-center">
              <div className="text-3xl mb-2">🔍</div>
              <h2 className="text-xl font-black text-navy-dark mb-1">شفافية كاملة</h2>
              <p className="text-sm text-gray-600 max-w-2xl mx-auto">
                للاستعلام عن أي ترخيص أو سجل، تواصل معنا على{' '}
                <a href="mailto:info@img.sa" className="text-navy hover:underline" dir="ltr">info@img.sa</a>
                {' '}أو{' '}
                <a href="tel:0114993666" className="text-navy hover:underline" dir="ltr">0114993666</a>.
              </p>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* مودال عرض الصورة كاملة */}
      {opened && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setOpenId(null)}
        >
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-start justify-between gap-3">
              <div>
                <h3 className="font-black text-navy-dark">{opened.title}</h3>
                <div className="text-xs text-gray-500 mt-1">{CATEGORY_LABEL[opened.category] ?? opened.category}</div>
              </div>
              <button onClick={() => setOpenId(null)} className="text-gray-400 hover:text-gray-700 text-xl leading-none">✕</button>
            </div>
            <div className="p-4 bg-gray-50">
              <img
                src={opened.imageUrl}
                alt={opened.title}
                className="w-full max-h-[70vh] object-contain mx-auto rounded-lg"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent && !parent.querySelector('.img-fallback-modal')) {
                    const fb = document.createElement('div');
                    fb.className = 'img-fallback-modal py-16 text-center';
                    fb.innerHTML = `
                      <div class="text-7xl mb-3">📜</div>
                      <div class="text-navy-dark font-black mb-1">${opened.title}</div>
                      <div class="text-xs text-gray-500">صورة الوثيقة قيد التحديث</div>
                    `;
                    parent.appendChild(fb);
                  }
                }}
              />
            </div>
            {(opened.description || opened.issuedBy || opened.referenceNo || opened.issuedAt || opened.expiresAt) && (
              <div className="p-4 text-sm text-gray-700 space-y-1.5">
                {opened.description && <p className="leading-relaxed">{opened.description}</p>}
                {opened.issuedBy && <div>🏛️ <b>الجهة المُصدِرة:</b> {opened.issuedBy}</div>}
                {opened.referenceNo && <div>🔖 <b>رقم الوثيقة:</b> <span dir="ltr">{opened.referenceNo}</span></div>}
                {opened.issuedAt && <div>📅 <b>تاريخ الإصدار:</b> {new Date(opened.issuedAt).toLocaleDateString('ar-SA')}</div>}
                {opened.expiresAt && <div>⏰ <b>تاريخ الانتهاء:</b> {new Date(opened.expiresAt).toLocaleDateString('ar-SA')}</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
